import React, { useState, useEffect } from 'react';
import {
        Paper,
        Typography,
        Box,
        Button,
        Dialog,
        DialogTitle,
        DialogContent,
        TextField,
        CircularProgress,
        Stack,
        Chip,
        IconButton
} from '@mui/material';
import axios from 'axios';
import Confetti from 'react-confetti';
import { ReviewIntegrations } from '../settings/ReviewIntegrations';

interface SentimentData {
        state: 'happy' | 'sad' | 'error';
        icon: string;
        message: string;
        unreplied_count: number;
        urgent_review?: {
                id: string;
                author: string;
                content: string;
                logo: string; // 'G' or 'Y'
        };
}

export const TamagotchiWidget: React.FC = () => {
        const [data, setData] = useState<SentimentData | null>(null);
        const [loading, setLoading] = useState(true);
        const [replyOpen, setReplyOpen] = useState(false);
        const [replyText, setReplyText] = useState('');
        const [submitting, setSubmitting] = useState(false);
        const [celebrating, setCelebrating] = useState(false);
        const [settingsOpen, setSettingsOpen] = useState(false);

        useEffect(() => {
                fetchSentiment();
        }, []);

        const fetchSentiment = async () => {
                try {
                        // Customer ID mock
                        const res = await axios.get('/api/cfo/reviews/sentiment/test-customer-123');
                        setData(res.data);
                } catch (err) {
                        console.error("Failed to fetch sentiment", err);
                } finally {
                        setLoading(false);
                }
        };

        const handleClean = () => {
                if (data?.urgent_review) {
                        // Auto-generate a draft reply (Mock AI)
                        setReplyText(`Hi ${data.urgent_review.author}, thank you for your feedback! We're sorry to hear about your experience and would love to make it right.`);
                        setReplyOpen(true);
                }
        };

        const submitReply = async () => {
                if (!data?.urgent_review) return;

                setSubmitting(true);
                try {
                        await axios.post('/api/cfo/reviews/reply/test-customer-123', {
                                review_id: data.urgent_review.id,
                                reply_text: replyText
                        });

                        setReplyOpen(false);
                        setCelebrating(true);
                        setTimeout(() => {
                                setCelebrating(false);
                                fetchSentiment(); // Refresh state
                        }, 3000);

                } catch (e) {
                        console.error("Reply failed", e);
                } finally {
                        setSubmitting(false);
                }
        };

        if (loading) return <CircularProgress size={20} />;
        if (!data) return null;

        return (
                <Paper
                        elevation={3}
                        sx={{
                                p: 2,
                                height: '100%',
                                borderRadius: 4,
                                background: data.state === 'happy'
                                        ? 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)'
                                        : 'linear-gradient(135deg, #fff3e0 0%, #ffffff 100%)',
                                textAlign: 'center',
                                position: 'relative',
                                overflow: 'hidden'
                        }}
                >
                        {celebrating && <Confetti numberOfPieces={100} recycle={false} />}

                        <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                                <IconButton size="small" onClick={() => setSettingsOpen(true)}>
                                        <span role="img" aria-label="settings">⚙️</span>
                                </IconButton>
                        </Box>

                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                                CUSTOMER PULSE
                        </Typography>

                        <Box sx={{ fontSize: '60px', my: 2, animation: 'bounce 2s infinite' }}>
                                {data.icon}
                        </Box>

                        <Typography variant="h6" fontWeight="bold">
                                {data.message}
                        </Typography>

                        {/* Action Area */}
                        {data.state === 'sad' && (
                                <Box sx={{ mt: 2 }}>
                                        <Button
                                                variant="contained"
                                                color="warning"
                                                onClick={handleClean}
                                                sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 'bold' }}
                                        >
                                                Clean Shop (Reply to {data.unreplied_count})
                                        </Button>
                                </Box>
                        )}

                        {data.state === 'happy' && (
                                <Box sx={{ mt: 2 }}>
                                        <Chip label="Level 5 Shopkeeper" color="primary" size="small" variant="outlined" />
                                </Box>
                        )}

                        {/* Reply Dialog */}
                        <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} maxWidth="sm" fullWidth>
                                <DialogTitle>Reply to {data.urgent_review?.author}</DialogTitle>
                                <DialogContent>
                                        {data.urgent_review && (
                                                <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 3 }}>
                                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                                "{data.urgent_review.content}"
                                                        </Typography>
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                                                {[...Array(data.urgent_review.content.length > 50 ? 2 : 5)].map((_, i) => (
                                                                        <span key={i}>⭐</span>
                                                                ))}
                                                        </Box>
                                                </Box>
                                        )}

                                        <TextField
                                                label="Your Reply"
                                                multiline
                                                rows={4}
                                                fullWidth
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                        />

                                        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
                                                <Button onClick={() => setReplyOpen(false)}>Cancel</Button>
                                                <Button
                                                        variant="contained"
                                                        onClick={submitReply}
                                                        disabled={submitting}
                                                >
                                                        {submitting ? 'Sending...' : 'Post Reply (+50 XP)'}
                                                </Button>
                                        </Stack>
                                </DialogContent>
                        </Dialog>
                </Paper>
        );
};
